import { isSidePatchActive, SIDE_PATCH_COLOR_COMPONENT_IDS } from "./sidePatchState";

/**
 * Calculates the final price of the configuration.
 *
 * Pricing model (B2B catalogs discount base + add-on variants in Shopify):
 * - shopify.price and item prices are already in presentment currency at the
 *   amounts shown in the builder (discounted for dealers).
 * - shopify.msrp is shop-currency (usually USD); converted via currency.rate.
 * - Display / pay preview = price + items (what the buyer expects to pay).
 * - Cart product variant must be created at full MSRP so Shopify B2B (or retail
 *   discount codes) can apply the discount once — not on an already-discounted price.
 * - MSRP total = (price + items) * (msrp / price) when a real base discount exists.
 *
 * @param {boolean} returnMsrpTotal - When true, return full pre-discount MSRP.
 *   When false (default), return what the customer pays (price + items).
 */
export const getFinalPrice = (productsState, componentsState, itemsState, returnMsrpTotal = false) => {
  // CUSTOM CODE
  const msrpRaw = productsState.activeObj?.shopify?.msrp;
  const price = productsState.activeObj?.shopify?.price;
  const isDealer = !!productsState.activeObj?.shopify?.isDealer;
  let itemsPrice = 0;
  const itemPriceBreakdown = [];
  const sidePatchActive = isSidePatchActive({ itemsState, productsState });

  for (let key in itemsState.activeObjs) {
    const item = itemsState.activeObjs[key];
    const isExcluded = !!componentsState.array.find((component) => component._id === key)?.excluded;
    // Side patch colors / patch add-on must not contribute unless the patch is fully active
    if (SIDE_PATCH_COLOR_COMPONENT_IDS.includes(key) && !sidePatchActive) continue;
    if (key === "custom-patches-side" && !sidePatchActive) continue;
    if (item?.price && !isExcluded) {
      itemsPrice += item.price;
      itemPriceBreakdown.push({ key, price: item.price, displayName: item.displayName });
    }
  }

  const chargedTotal = price + itemsPrice;
  const currencyRate = window._tt?.shopifyData?.currency?.rate;

  console.groupCollapsed(
    `[getFinalPrice] returnMsrpTotal=${returnMsrpTotal} isDealer=${isDealer}`,
  );
  console.log("inputs", {
    msrpRaw,
    price,
    itemsPrice,
    chargedTotal,
    itemPriceBreakdown,
    currencyRate,
  });

  // Inflate already-discounted config back to MSRP using base seat ratio
  if (returnMsrpTotal && msrpRaw) {
    const msrp = convertMsrpToNativeCurrency(msrpRaw);
    if (msrp !== 0 && hasBaseDiscount(price, msrp)) {
      const inflateMultiplier = msrp / price; // e.g. 90/63 ≈ 1.4286 for 30% off
      const discountPercentage = (1 - price / msrp) * 100;
      const msrpTotal = chargedTotal * inflateMultiplier;
      const result = msrpTotal.toFixed(2);

      console.log("MSRP inflate (all Shopify prices treated as discounted)", {
        formula: "msrpTotal = (price + itemsPrice) * (msrp / price)",
        msrp,
        price,
        discountPercentage: `${discountPercentage.toFixed(2)}%`,
        inflateMultiplier,
        chargedTotal,
        msrpTotal,
        result,
      });
      console.groupEnd();
      return result;
    }

    const result = chargedTotal.toFixed(2);
    console.log("returnMsrpTotal but no meaningful base discount; using charged total", {
      msrp,
      price,
      result,
    });
    console.groupEnd();
    return result;
  }

  const result = chargedTotal.toFixed(2);
  console.log("charged / display total", { price, itemsPrice, chargedTotal, result });
  console.groupEnd();
  return result;
};

/** Below this relative gap, a price/msrp difference is FX noise rather than a dealer discount. */
const MIN_DISCOUNT_RATIO = 0.01;

/**
 * Currency conversion (msrp * rate) yields floats and Shopify Markets rounds
 * presentment prices, so exact equality would report phantom discounts in
 * non-USD sessions. Real dealer discounts are far larger than this margin.
 *
 * Directional on purpose: a price above MSRP is bad data, and treating it as a
 * discount would deflate the config below what the buyer is charged.
 */
const hasBaseDiscount = (price, msrp) => {
  if (!msrp || !price) return false;
  return (msrp - price) / msrp > MIN_DISCOUNT_RATIO;
};

/** Dealer discount % from base msrp vs price (e.g. 30), or null if not applicable. */
export const getDealerDiscountPercentage = (shopify) => {
  if (!shopify?.isDealer || !shopify?.msrp || shopify.price == null) return null;
  const msrp = convertMsrpToNativeCurrency(shopify.msrp);
  if (!msrp || !hasBaseDiscount(shopify.price, msrp)) return 0;
  return Number(((1 - shopify.price / msrp) * 100).toFixed(2));
};

export const convertToUSD = (price) => {
  const conversionRate = window._tt?.shopifyData?.currency?.rate;
  if (!conversionRate) return price;
  return (parseFloat(price) / parseFloat(conversionRate)).toFixed(2);
};

export const convertMsrpToNativeCurrency = (msrp) => {
  const conversionRate = window._tt?.shopifyData?.currency?.rate;
  if (!conversionRate) return msrp;
  return msrp * parseFloat(conversionRate);
};
